import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Switch from '@mui/material/Switch';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import Container from '../../components/Container';
import * as Api from '../../api';
import ProductForm from './ProductForm';

const O = {
  forest:     '#053418',
  accent:     '#00c853',
  accentSoft: '#d6f1d8',
  ink:        '#1a1a1a',
  muted:      '#6b7368',
  muted2:     '#9aa39a',
  line:       '#e6e8df',
  cream:      '#fafaf7',
  cream50:    '#fcfcfa',
  white:      '#ffffff',
  shadowCard: '0 1px 0 0 rgba(10,40,24,0.04), 0 12px 32px -16px rgba(10,40,24,0.18)',
};

const COUNTRY_SYMBOL = { GB: '£', US: '$', EU: '€', AE: 'AED', SA: '﷼', BH: 'BD', IN: '₹', QA: 'QR', AU: 'A$', CA: 'C$' };
const TYPE_STYLE = {
  bundle: { bg: '#d6f1d8', color: '#053418' },
  core:   { bg: '#e8f0fe', color: '#1a3c8b' },
  addon:  { bg: '#fef3c7', color: '#92400e' },
};

// ─── Price display helper ───────────────────────────────────────────────────
function formatPricingRow(row) {
  const sym = row.currency;
  const parts = [];
  if (row.amount != null) parts.push(`${sym} ${row.amount} / ${row.billingCycle}`);
  if (row.percent != null) parts.push(`${row.percent}% transaction`);
  if (row.notes) parts.push(`(${row.notes})`);
  return parts.join(' + ') || '—';
}

// ─── Status chip ────────────────────────────────────────────────────────────
function StatusChip({ status }) {
  const map = {
    active:  { bg: O.accentSoft, color: O.forest, label: 'Active' },
    expired: { bg: '#fde8e8',    color: '#8b1a1a', label: 'Expired' },
  };
  const s = map[status] || { bg: '#f0f0f0', color: O.muted, label: status };
  return <Chip label={s.label} size="small" sx={{ height: 20, fontFamily: 'GilroySemiBold', fontSize: 11, backgroundColor: s.bg, color: s.color }} />;
}

// ─── Product row ─────────────────────────────────────────────────────────────
function ProductCard({ product, columns, onEdit, onDelete }) {
  const type = product.type || (product.isBundle ? 'bundle' : 'core');
  const typeStyle = TYPE_STYLE[type] || { bg: O.cream, color: O.muted };
  const children = product.includesProducts || product.includes || [];

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', px: 2.5, py: 1.75, borderBottom: `1px solid ${O.line}`, '&:last-child': { borderBottom: 0 }, '&:hover': { backgroundColor: O.cream } }}>
      {/* Name + slug + type badge */}
      <Box sx={{ flex: '0 0 240px', pr: 2 }}>
        <Typography sx={{ fontFamily: 'GilroyBold', fontSize: 13.5, color: O.forest, lineHeight: 1.3 }}>{product.name || product.productName}</Typography>
        <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 11.5, color: O.muted, mt: 0.2 }}>{product.key || product.productKey}</Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', mt: 0.6, px: 1, py: 0.25, borderRadius: '5px', backgroundColor: typeStyle.bg }}>
          <Typography sx={{ fontFamily: 'GilroySemiBold', fontSize: 10.5, color: typeStyle.color, textTransform: 'capitalize' }}>{type}</Typography>
        </Box>
        {children.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.6 }}>
            {children.map((inc, i) => (
              <Chip key={i} label={typeof inc === 'string' ? inc : (inc.name || inc.productName || inc.key || inc.productKey)} size="small"
                sx={{ height: 17, fontFamily: 'GilroySemiBold', fontSize: 10, backgroundColor: O.accentSoft, color: O.forest }} />
            ))}
          </Box>
        )}
      </Box>

      {/* One cell per country column */}
      {columns.map(cc => {
        const entries = (product.pricing || []).filter(r => r.countryCode === cc);
        return (
          <Box key={cc} sx={{ flex: '0 0 140px', pr: 1, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
            {entries.length === 0
              ? <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 13, color: O.line }}>—</Typography>
              : entries.map((row, i) => (
                <Box key={i}>
                  <Typography sx={{ fontFamily: 'GilroySemiBold', fontSize: 13, color: O.ink }}>{formatPricingRow(row)}</Typography>
                  {row.notes && <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 11, color: O.muted }}>{row.notes}</Typography>}
                </Box>
              ))
            }
          </Box>
        );
      })}

      {/* Actions */}
      <Box sx={{ flex: '0 0 auto', display: 'flex', gap: 0.5, ml: 'auto' }}>
        <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(product)} sx={{ color: O.muted, '&:hover': { color: O.forest, backgroundColor: O.accentSoft } }}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Delete"><IconButton size="small" onClick={() => onDelete(product)} sx={{ color: O.muted, '&:hover': { color: '#c0392b', backgroundColor: '#fde8e8' } }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
      </Box>
    </Box>
  );
}

// ─── Venue Subscriptions tab ─────────────────────────────────────────────────
function VenueSubscriptions({ allProducts }) {
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRest, setSelectedRest] = useState('');
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [editSub, setEditSub] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [grantForm, setGrantForm] = useState({ productKey: '', startDate: '', endDate: '', autoRenew: true });

  useEffect(() => {
    Api.restaurantList('').then(res => {
      if (res?.success) setRestaurants(res.data?.rows || res.data || []);
    }).catch(() => {});
  }, []);

  const loadSubs = useCallback((restId) => {
    if (!restId) return;
    setLoading(true);
    Api.listVenueSubscriptions(restId).then(res => {
      setSubs(res?.data?.rows || []);
    }).catch(() => setSubs([])).finally(() => setLoading(false));
  }, []);

  const handleRestChange = (e) => {
    setSelectedRest(e.target.value);
    loadSubs(e.target.value);
  };

  const openGrant = () => {
    setGrantForm({ productKey: '', startDate: '', endDate: '', autoRenew: true });
    setEditSub(null);
    setGrantOpen(true);
  };

  const openEdit = (sub) => {
    setEditSub(sub);
    setGrantForm({
      productKey: sub.productKey || '',
      startDate: sub.startDate ? sub.startDate.split('T')[0] : '',
      endDate: sub.endDate ? sub.endDate.split('T')[0] : '',
      autoRenew: sub.autoRenew,
    });
    setGrantOpen(true);
  };

  const handleSaveGrant = () => {
    if (!selectedRest) return;
    setSaving(true);
    const payload = editSub ? {
      startDate: grantForm.startDate || undefined,
      endDate: grantForm.endDate || undefined,
      autoRenew: grantForm.autoRenew,
    } : {
      productKey: grantForm.productKey,
      startDate: grantForm.startDate || undefined,
      endDate: grantForm.endDate || undefined,
      autoRenew: grantForm.autoRenew,
    };
    const call = editSub
      ? Api.patchVenueSubscription(selectedRest, editSub.productKey, payload)
      : Api.grantVenueSubscription(selectedRest, payload);
    call.then(() => {
      setGrantOpen(false);
      loadSubs(selectedRest);
    }).catch(() => {}).finally(() => setSaving(false));
  };

  const handleRevoke = () => {
    if (!revokeTarget || !selectedRest) return;
    setSaving(true);
    Api.revokeVenueSubscription(selectedRest, revokeTarget.productKey)
      .then(() => { setRevokeTarget(null); loadSubs(selectedRest); })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 320 }}>
          <InputLabel sx={{ fontFamily: 'GilroyRegular', fontSize: 13 }}>Select restaurant</InputLabel>
          <Select value={selectedRest} label="Select restaurant" onChange={handleRestChange} sx={inputSx}>
            {restaurants.map(r => (
              <MenuItem key={r._id} value={r._id}>
                <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 13 }}>{r.name || r.restaurantName}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedRest && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openGrant} disableElevation
            sx={{ fontFamily: 'GilroySemiBold', fontSize: 13, textTransform: 'none', borderRadius: '9px', backgroundColor: O.accent, color: O.forest, '&:hover': { backgroundColor: '#00a847' } }}>
            Grant subscription
          </Button>
        )}
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: O.accent }} /></Box>}

      {!loading && selectedRest && (
        subs.length === 0
          ? <Box sx={{ textAlign: 'center', py: 8 }}><Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 14, color: O.muted }}>No active subscriptions for this venue.</Typography></Box>
          : (
            <Paper elevation={0} sx={{ border: `1px solid ${O.line}`, borderRadius: '12px', overflow: 'hidden', backgroundColor: O.white }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: O.cream50 }}>
                      {['Product', 'Price', 'Start', 'End', 'Renewal', 'Auto-renew', 'Status', ''].map(h => (
                        <TableCell key={h} sx={{ fontFamily: 'GilroySemiBold', fontSize: 12, color: O.muted, borderBottom: `1px solid ${O.line}`, py: 1.5 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subs.map(sub => (
                      <TableRow key={sub._id} sx={{ '&:hover': { backgroundColor: O.cream }, '&:last-child td': { border: 0 } }}>
                        <TableCell sx={{ borderBottom: `1px solid ${O.line}` }}>
                          <Typography sx={{ fontFamily: 'GilroySemiBold', fontSize: 13, color: O.forest }}>{sub.productName}</Typography>
                          <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 11, color: O.muted }}>{sub.productKey}</Typography>
                          {sub.includes?.length > 0 && (
                            <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 11, color: O.muted, mt: 0.25 }}>
                              Includes: {sub.includes.map(i => i.productName).join(', ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'GilroyRegular', fontSize: 12.5, color: O.ink, borderBottom: `1px solid ${O.line}` }}>
                          {sub.price ? formatPricingRow({ ...sub.price, currency: sub.price.currency }) : '—'}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'GilroyRegular', fontSize: 12.5, color: O.muted, borderBottom: `1px solid ${O.line}` }}>{sub.startDate ? sub.startDate.split('T')[0] : '—'}</TableCell>
                        <TableCell sx={{ fontFamily: 'GilroyRegular', fontSize: 12.5, color: O.muted, borderBottom: `1px solid ${O.line}` }}>{sub.endDate ? sub.endDate.split('T')[0] : '—'}</TableCell>
                        <TableCell sx={{ fontFamily: 'GilroyRegular', fontSize: 12.5, color: O.muted, borderBottom: `1px solid ${O.line}` }}>{sub.renewalDate ? sub.renewalDate.split('T')[0] : '—'}</TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${O.line}` }}>
                          <Chip label={sub.autoRenew ? 'Yes' : 'No'} size="small" sx={{ height: 20, fontFamily: 'GilroySemiBold', fontSize: 11, backgroundColor: sub.autoRenew ? O.accentSoft : '#f0f0f0', color: sub.autoRenew ? O.forest : O.muted }} />
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${O.line}` }}><StatusChip status={sub.status} /></TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${O.line}` }}>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(sub)} sx={{ color: O.muted, '&:hover': { color: O.forest } }}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Revoke"><IconButton size="small" onClick={() => setRevokeTarget(sub)} sx={{ color: O.muted, '&:hover': { color: '#c0392b' } }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )
      )}

      {/* Grant / Edit dialog */}
      <Dialog open={grantOpen} onClose={() => setGrantOpen(false)} PaperProps={{ sx: { borderRadius: '14px', width: 460 } }}>
        <DialogTitle sx={{ fontFamily: 'GilroyBold', fontSize: 16, color: O.forest, pb: 1 }}>
          {editSub ? 'Edit subscription' : 'Grant subscription'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          {!editSub && (
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontFamily: 'GilroyRegular', fontSize: 13 }}>Product</InputLabel>
              <Select value={grantForm.productKey} label="Product" onChange={e => setGrantForm(f => ({ ...f, productKey: e.target.value }))} sx={inputSx}>
                {allProducts.map(p => (
                  <MenuItem key={p._id || p.key} value={p.key || p.productKey}>
                    <Typography sx={{ fontFamily: 'GilroySemiBold', fontSize: 13 }}>{p.name || p.productName}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField label="Start date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={grantForm.startDate} onChange={e => setGrantForm(f => ({ ...f, startDate: e.target.value }))} sx={inputSx} />
          <TextField label="End date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={grantForm.endDate} onChange={e => setGrantForm(f => ({ ...f, endDate: e.target.value }))} sx={inputSx} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontFamily: 'GilroySemiBold', fontSize: 13, color: O.ink }}>Auto-renew</Typography>
            <Switch checked={grantForm.autoRenew} onChange={e => setGrantForm(f => ({ ...f, autoRenew: e.target.checked }))} sx={{ '& .MuiSwitch-thumb': { backgroundColor: grantForm.autoRenew ? O.accent : undefined } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setGrantOpen(false)} sx={{ fontFamily: 'GilroySemiBold', fontSize: 13, textTransform: 'none', color: O.muted }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveGrant} disabled={saving} disableElevation
            sx={{ fontFamily: 'GilroyBold', fontSize: 13, textTransform: 'none', borderRadius: '8px', backgroundColor: O.accent, color: O.forest, '&:hover': { backgroundColor: '#00a847' } }}>
            {saving ? <CircularProgress size={16} sx={{ color: O.forest }} /> : editSub ? 'Save' : 'Grant'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirm */}
      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)} PaperProps={{ sx: { borderRadius: '14px', width: 400 } }}>
        <DialogTitle sx={{ fontFamily: 'GilroyBold', fontSize: 16, color: '#8b1a1a' }}>Revoke subscription?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 13.5, color: O.ink }}>
            This will immediately revoke <strong>{revokeTarget?.productName}</strong> for this venue. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setRevokeTarget(null)} sx={{ fontFamily: 'GilroySemiBold', fontSize: 13, textTransform: 'none', color: O.muted }}>Cancel</Button>
          <Button variant="contained" onClick={handleRevoke} disabled={saving} disableElevation
            sx={{ fontFamily: 'GilroyBold', fontSize: 13, textTransform: 'none', borderRadius: '8px', backgroundColor: '#c0392b', color: '#fff', '&:hover': { backgroundColor: '#922b21' } }}>
            {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function Subscriptions(props) {
  const [tab, setTab] = useState(0);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'error' });

  const fetchProducts = useCallback(() => {
    setLoadingProducts(true);
    Api.listSubscriptionProducts().then(res => {
      setProducts(res?.data?.rows || res?.data || []);
    }).catch(() => setProducts([])).finally(() => setLoadingProducts(false));
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openCreate = () => { setEditProduct(null); setFormOpen(true); };
  const openEdit = (p) => { setEditProduct(p); setFormOpen(true); };

  const handleSave = (payload) => {
    setSaving(true);
    const call = editProduct
      ? Api.updateSubscriptionProduct(editProduct.key || editProduct.productKey, payload)
      : Api.createSubscriptionProduct(payload);
    call.then((res) => {
      if (res && res.success === false) {
        setSnack({ open: true, msg: res.msg || 'Failed to save product', severity: 'error' });
        return;
      }
      setFormOpen(false);
      fetchProducts();
    })
      .catch((err) => {
        setSnack({ open: true, msg: err?.message || 'Failed to save product', severity: 'error' });
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    setSaving(true);
    Api.deleteSubscriptionProduct(deleteTarget.key || deleteTarget.productKey)
      .then((res) => {
        if (res && res.success === false) {
          setSnack({ open: true, msg: res.msg || 'Cannot delete — revoke active subscriptions first', severity: 'error' });
          return;
        }
        setDeleteTarget(null);
        fetchProducts();
      })
      .catch((err) => {
        setSnack({ open: true, msg: err?.message || 'Delete failed', severity: 'error' });
      })
      .finally(() => setSaving(false));
  };

  const filtered = products.filter(p => {
    if (!search) return true;
    const name = (p.name || p.productName || '').toLowerCase();
    const key = (p.key || p.productKey || '').toLowerCase();
    return name.includes(search.toLowerCase()) || key.includes(search.toLowerCase());
  });
  const bundles = filtered.filter(p => p.type === 'bundle' || p.isBundle);
  const standalone = filtered.filter(p => p.type !== 'bundle' && !p.isBundle);
  const countryCols = [...new Set(filtered.flatMap(p => (p.pricing || []).map(r => r.countryCode).filter(Boolean)))];

  return (
    <Container {...props} page="subscriptions" onSearch={() => {}}>
      <Box sx={{ maxWidth: 1300 }}>
        {/* Page header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography sx={{ fontFamily: 'GilroyBold', fontSize: 26, color: '#053418' }}>Subscriptions</Typography>
            <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 13.5, color: '#6b7368', mt: 0.5 }}>
              Manage the product catalogue and per-venue subscription grants.
            </Typography>
          </Box>
          {tab === 0 && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disableElevation
              sx={{ fontFamily: 'GilroyBold', fontSize: 13.5, textTransform: 'none', borderRadius: '10px', backgroundColor: O.accent, color: O.forest, px: 2.5, py: 1, '&:hover': { backgroundColor: '#00a847' } }}>
              Add product
            </Button>
          )}
        </Box>

        {/* Tabs */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: `1px solid ${O.line}`, mb: 3, '& .MuiTabs-indicator': { backgroundColor: O.accent } }}>
          <StyledTab icon={<InventoryOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Product Catalogue" />
          <StyledTab icon={<StorefrontOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Venue Subscriptions" />
        </Tabs>

        {/* ── Catalogue tab ── */}
        {tab === 0 && (
          <Box>
            {loadingProducts
              ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: O.accent }} /></Box>
              : products.length === 0
                ? (
                  <Box sx={{ textAlign: 'center', py: 10, backgroundColor: O.white, borderRadius: '12px', border: `1px solid ${O.line}` }}>
                    <InventoryOutlinedIcon sx={{ fontSize: 40, color: O.line, mb: 1 }} />
                    <Typography sx={{ fontFamily: 'GilroyBold', fontSize: 15, color: O.forest, mb: 0.5 }}>No products yet</Typography>
                    <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 13, color: O.muted, mb: 2.5 }}>Create your first subscription product to get started.</Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disableElevation
                      sx={{ fontFamily: 'GilroyBold', fontSize: 13, textTransform: 'none', borderRadius: '9px', backgroundColor: O.accent, color: O.forest }}>
                      Add product
                    </Button>
                  </Box>
                )
                : (
                  <Paper elevation={0} sx={{ border: `1px solid ${O.line}`, borderRadius: '12px', overflow: 'hidden', backgroundColor: O.white }}>
                    {/* List header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 1.5, backgroundColor: O.forest, borderBottom: `1px solid ${O.line}` }}>
                      <Typography sx={{ flex: '0 0 240px', fontFamily: 'GilroyBold', fontSize: 12, color: O.white, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Product</Typography>
                      {countryCols.map(cc => (
                        <Typography key={cc} sx={{ flex: '0 0 140px', fontFamily: 'GilroyBold', fontSize: 12, color: O.white, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          {COUNTRY_SYMBOL[cc] || cc}
                        </Typography>
                      ))}
                      <Box sx={{ flex: '0 0 72px', ml: 'auto' }} />
                    </Box>
                    {filtered.map(p => (
                      <ProductCard key={p._id || p.key} product={p} columns={countryCols} onEdit={openEdit} onDelete={setDeleteTarget} />
                    ))}
                  </Paper>
                )
            }
          </Box>
        )}

        {/* ── Venue Subscriptions tab ── */}
        {tab === 1 && <VenueSubscriptions allProducts={products} />}
      </Box>

      {/* Product form drawer */}
      <ProductForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
        product={editProduct}
        allProducts={products}
        saving={saving}
      />

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ fontFamily: 'GilroySemiBold', fontSize: 13 }}>{snack.msg}</Alert>
      </Snackbar>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} PaperProps={{ sx: { borderRadius: '14px', width: 420 } }}>
        <DialogTitle sx={{ fontFamily: 'GilroyBold', fontSize: 16, color: '#8b1a1a' }}>Delete product?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 13.5, color: O.ink }}>
            Permanently delete <strong>{deleteTarget?.name || deleteTarget?.productName}</strong>? Venues with active grants of this product may be affected.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ fontFamily: 'GilroySemiBold', fontSize: 13, textTransform: 'none', color: O.muted }}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} disabled={saving} disableElevation
            sx={{ fontFamily: 'GilroyBold', fontSize: 13, textTransform: 'none', borderRadius: '8px', backgroundColor: '#c0392b', color: '#fff', '&:hover': { backgroundColor: '#922b21' } }}>
            {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

function StyledTab(props) {
  return (
    <Tab
      {...props}
      sx={{
        fontFamily: 'GilroySemiBold',
        fontSize: 13.5,
        textTransform: 'none',
        color: '#6b7368',
        minHeight: 44,
        gap: 0.5,
        '&.Mui-selected': { color: '#053418' },
      }}
    />
  );
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fcfcfa',
    borderRadius: '8px',
    fontFamily: 'GilroyRegular',
    fontSize: 13.5,
    '& fieldset': { borderColor: '#e6e8df' },
    '&:hover fieldset': { borderColor: '#9aa39a' },
    '&.Mui-focused fieldset': { borderColor: '#053418', borderWidth: '1.5px' },
  },
  '& .MuiInputLabel-root': { fontFamily: 'GilroyRegular', fontSize: 13 },
  '& .MuiInputLabel-root.Mui-focused': { color: '#053418' },
};
